// import components
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';
import ColorCell from 'components/shared/Table/TableCell/DataGrid/ColorCell';
import DateCell from 'components/shared/Table/TableCell/DataGrid/DateCell';

// import external libraries
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import hooks
import useGetFunctionGroupFilterOptions from './useGetFunctionGroupFilterOptions';

// import types
import { GridAlignment, GridColDef, ValueOptions } from '@mui/x-data-grid-pro';

export const commonCenteredProps: {
  align: GridAlignment;
  headerAlign: GridAlignment;
} = { align: 'center', headerAlign: 'center' };

const useColumnDefinitions = () => {
  const { t } = useTranslation();

  const functionGroups = useGetFunctionGroupFilterOptions();

  return useMemo(() => {
    return generateColumns(t, functionGroups);
  }, [t, functionGroups]);
};

const generateColumns = (
  t: TFunction,
  functionGroups: ValueOptions[]
): GridColDef[] => [
  {
    field: 'code',
    headerName: t('functions.fields.code'),
    type: 'string',
    width: 70,
    hideable: false
  },
  {
    field: 'description',
    width: 160,
    headerName: t('functions.fields.description')
  },
  {
    field: 'colorCode',
    headerName: t('functions.fields.colorCode'),
    renderCell: ColorCell,
    width: 50,
    ...commonCenteredProps
  },
  {
    field: 'codeExt',
    headerName: t('functions.fields.codeExt'),
    type: 'string',
    width: 90,
    ...commonCenteredProps
  },
  {
    field: 'codeExtSecondary',
    headerName: t('functions.fields.codeExtSecondary'),
    type: 'string',
    width: 90,
    ...commonCenteredProps
  },
  {
    field: 'costKind',
    headerName: t('functions.fields.costKind'),
    width: 90,
    type: 'number',
    ...commonCenteredProps
  },
  {
    field: 'isGroup',
    headerName: t('functions.fields.flags.FUNCTION_GROUP'),
    type: 'boolean',
    width: 50,
    renderCell: BooleanCell,
    ...commonCenteredProps
  },
  {
    field: 'functionGroup',
    type: 'singleSelect',
    headerName: t('functions.fields.parentFunctionId'),
    width: 90,
    valueOptions: functionGroups,
    ...commonCenteredProps
  },
  {
    field: 'noPicker',
    headerName: t('functions.fields.flags.REQUEST_EXCLUDE'),
    type: 'boolean',
    width: 100,
    renderCell: BooleanCell,
    ...commonCenteredProps
  },
  {
    field: 'excludeCapPlan',
    headerName: t('functions.fields.flags.CAPPLAN_EXCLUDE'),
    type: 'boolean',
    width: 100,
    renderCell: BooleanCell,
    ...commonCenteredProps
  },
  {
    field: 'noBUH',
    headerName: t('functions.fields.flags.BUH_BLOCKED'),
    type: 'boolean',
    width: 70,
    renderCell: BooleanCell,
    ...commonCenteredProps
  },
  {
    field: 'noBonus',
    headerName: t('functions.fields.flags.BONUS_BLOCKED'),
    type: 'boolean',
    width: 80,
    renderCell: BooleanCell,
    ...commonCenteredProps
  },
  {
    field: 'leaveBlocked',
    headerName: t('functions.fields.flags.LEAVE_BLOCKED'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'salaryBlocked',
    headerName: t('functions.fields.flags.SALARY_BLOCKED'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'overlap',
    headerName: t('functions.fields.flags.DUTY_OVERLAP_ALLOWED'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'includeInKidsRatio',
    headerName: t('functions.fields.flags.INCLUDE_IN_KIDS_RATIO'),
    type: 'boolean',
    renderCell: BooleanCell
  },
  {
    field: 'valid.from',
    width: 90,
    headerName: t('validFrom'),
    type: 'date',
    renderCell: ({ row: { valid } }) => <DateCell value={valid.from} />
  },
  {
    field: 'valid.to',
    width: 90,
    headerName: t('validTo'),
    type: 'date',
    renderCell: ({ row: { valid } }) => <DateCell value={valid.to} />
  }
];

export default useColumnDefinitions;
